import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL, getCustomerToken, clearCustomerToken, customerFetch } from '../utils/api';

// Contexte global du site public : panier, compte fidélité, réglages et soirées.
const StoreContext = createContext(null);

let uidSeq = 1;
const newUid = () => `${Date.now()}-${uidSeq++}`;

// Normalise un panier chargé depuis localStorage (compat anciennes versions)
const normalizeCart = (raw) =>
  (Array.isArray(raw) ? raw : []).map(item => ({
    ...item,
    uid: item.uid || newUid(),
    redeemed: Boolean(item.redeemed),
    quantity: item.quantity || 1,
    notes: item.notes || ''
  }));

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      return normalizeCart(JSON.parse(localStorage.getItem('cart')));
    } catch {
      return [];
    }
  });
  const [customer, setCustomer] = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [loyaltyEvents, setLoyaltyEvents] = useState([]);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const refreshCustomer = useCallback(async () => {
    if (!getCustomerToken()) {
      setCustomer(null);
      return null;
    }
    try {
      const response = await customerFetch('/customers/me');
      if (response.ok) {
        const profile = await response.json();
        setCustomer(profile);
        return profile;
      }
      if (response.status === 401) {
        clearCustomerToken();
        setCustomer(null);
      }
    } catch (error) {
      console.error('Erreur profil fidélité:', error);
    }
    return null;
  }, []);

  useEffect(() => {
    refreshCustomer();
    (async () => {
      try {
        const [settingsRes, eventsRes] = await Promise.all([
          fetch(`${API_URL}/customers/loyalty/settings`),
          fetch(`${API_URL}/customers/loyalty/events`)
        ]);
        if (settingsRes.ok) setLoyaltySettings(await settingsRes.json());
        if (eventsRes.ok) setLoyaltyEvents(await eventsRes.json());
      } catch (error) {
        console.error('Erreur chargement fidélité:', error);
      }
    })();
  }, [refreshCustomer]);

  // Points déjà engagés par les lignes "récompense" du panier
  const pointsReserved = cart.reduce(
    (sum, item) => item.redeemed ? sum + (item.points_price || 0) * item.quantity : sum, 0
  );

  // --- Panier (euros) ---

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id && !i.redeemed && !i.notes);
      if (existing) {
        return prev.map(i => i.uid === existing.uid ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...product, uid: newUid(), quantity: 1, notes: '', redeemed: false }];
    });
  };

  // --- Récompenses (points) ---

  // Ajoute un article offert contre des points. Renvoie false si solde insuffisant.
  const addReward = (product) => {
    if (!customer || !product.points_price) return false;
    const cost = product.points_price;
    if (customer.balance - pointsReserved < cost) return false;

    setCart(prev => {
      const existing = prev.find(i => i.id === product.id && i.redeemed);
      if (existing) {
        return prev.map(i => i.uid === existing.uid ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...product, uid: newUid(), quantity: 1, notes: '', redeemed: true }];
    });
    return true;
  };

  const removeFromCart = (uid) => {
    setCart(prev => prev.filter(i => i.uid !== uid));
  };

  const updateQuantity = (uid, quantity) => {
    if (quantity <= 0) {
      removeFromCart(uid);
      return;
    }
    setCart(prev => {
      const line = prev.find(i => i.uid === uid);
      if (!line) return prev;
      // Sur une récompense, on empêche d'augmenter au-delà du solde disponible
      if (line.redeemed && quantity > line.quantity && customer) {
        const extraCost = (quantity - line.quantity) * (line.points_price || 0);
        if (customer.balance - pointsReserved < extraCost) return prev;
      }
      return prev.map(i => i.uid === uid ? { ...i, quantity } : i);
    });
  };

  const updateNotes = (uid, notes) => {
    setCart(prev => prev.map(i => i.uid === uid ? { ...i, notes } : i));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  const cartTotal = cart.reduce(
    (sum, item) => item.redeemed ? sum : sum + parseFloat(item.price) * item.quantity, 0
  );
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const logoutCustomer = () => {
    clearCustomerToken();
    setCustomer(null);
    // Retirer les récompenses du panier (plus de compte connecté)
    setCart(prev => prev.filter(i => !i.redeemed));
  };

  const value = {
    cart, addToCart, addReward, removeFromCart, updateQuantity, updateNotes, clearCart,
    cartTotal, cartCount, pointsReserved,
    customer, setCustomer, refreshCustomer, logoutCustomer,
    loyaltySettings, loyaltyEvents
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore doit être utilisé dans <StoreProvider>');
  return ctx;
};
